import { useEffect, useRef, useState } from "react";
import type { CapaRecord } from "./domain/models";
import type { FormErrors, FormKind } from "./capa-form";
import { validateForm } from "./capa-form";
import { capaGateway } from "./infrastructure/capa-gateway";

type Dependencies = { scopeKey: string; notify: (message: string) => void; load: () => Promise<void> };
export function useCapaFormWorkspace({ scopeKey, notify, load }: Dependencies) {
  const [formKind, setFormKind] = useState<FormKind>(null);
  const [selected, setSelected] = useState<CapaRecord | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const current = useRef({ scopeKey, generation: 0 });
  if (current.current.scopeKey !== scopeKey) current.current = { scopeKey, generation: current.current.generation + 1 };
  const formScope = useRef(scopeKey);
  const mounted = useRef(true);
  const locked = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setFormKind(null); setSelected(null); setForm({}); setFormErrors({}); }, [scopeKey]);
  const open = (kind: Exclude<FormKind, null>, record?: CapaRecord) => {
    current.current.generation++;
    formScope.current = scopeKey;
    setSelected(record ?? null); setForm({}); setFormErrors({}); setFormKind(kind);
  };
  const close = () => { current.current.generation++; setFormKind(null); };
  const field = (name: string, value: string) => {
    setForm(previous => ({ ...previous, [name]: value }));
    setFormErrors(previous => { if (!previous[name]) return previous; const next = { ...previous }; delete next[name]; return next; });
  };
  const submit = async () => {
    if (!formKind || locked.current || formScope.current !== scopeKey) return;
    const errors = validateForm(formKind, form);
    if (Object.keys(errors).length) { setFormErrors(errors); notify("Eksik veya kısa alanları kontrol edin."); return; }
    if (formKind !== "create" && !selected) { notify("İşlem yapılacak DÖF kaydı seçilmedi."); return; }
    const generation = current.current.generation;
    const isCurrent = () => mounted.current && current.current.generation === generation;
    locked.current = true;
    setSaving(true);
    try {
      if (formKind === "create") await capaGateway.create({ ...form, idempotencyKey: crypto.randomUUID() });
      if (formKind === "evaluate" && selected) await capaGateway.evaluate(selected.id, form);
      if (formKind === "complete" && selected) await capaGateway.completeAction(selected.id, form);
      if (formKind === "verify" && selected) await capaGateway.verify(selected.id, { effectivenessNote: form.effectivenessNote, effective: form.effective === "true" });
      if (!isCurrent()) return;
      notify(formKind === "create" ? "DÖF kaydı Kalite değerlendirmesine gönderildi." : "DÖF aşaması güncellendi.");
      setFormKind(null);
      await load();
    } catch (error) {
      if (isCurrent()) notify(error instanceof Error ? error.message : "DÖF işlemi tamamlanamadı.");
    } finally {
      locked.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  return { formKind: formScope.current === scopeKey ? formKind : null, selected, form, formErrors, saving, open, close, field, submit };
}
